<template>
  <div class="status-monitor" @click.stop="syncIfPossible">
    <div class="connectivity-type" :class="textColor">
      {{ connectionType }}
    </div>

    <div class="internet-connection">
      <v-icon x-small class="cloud-icon" :color="bottomNav.color">mdi-{{ isConnected ? 'cloud' : 'cloud-off-outline' }} </v-icon>
    </div>

    <div class="server-connection">
      <icon-text-btn
        :icon="`mdi-${isConnected && isServerReachable ? 'lan-connect' : 'lan-disconnect'}`"
        :hint="$t(isServerReachable ? 'serverReachable' : 'serverNotReachable')"
        small
        :iconColor="bottomNav.color"
        :width="20"
      ></icon-text-btn>
    </div>

    <div class="sync">
      <v-icon x-small :color="color(isSyncInProgress)"> mdi-sync {{ realSyncInProgress ? 'mdi-spin' : '' }}</v-icon>
    </div>

    <div class="sync-text" :class="textColor" v-html="syncText"></div>
    <div class="sync-duration" :class="textColor" v-if="!realSyncInProgress">{{ syncDuration }} Sekunden</div>
    <div :class="`vertical-separator ${theme.bottomNav.bg} lighten-1`"></div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import IconTextBtn from '@/components/buttons/IconTextBtn.vue';
import dateUtils from '@/common/dateUtils';
import common from '@/mixins/common';

export default {
  name: 'StatusMonitor',
  mixins: [common],
  components: {
    IconTextBtn,
  },
  props: {
    width: { type: Number, default: 100 },
  },
  computed: {
    ...mapState('sync', ['isSynchronizing', 'currentSync', 'lastSyncDateTime', 'syncDuration']),
    ...mapState(['isConnected', 'isServerReachable', 'connectionType', 'isToolbarsHidden']),
    ...mapState({
      isDisconnected: (state) => !state.isConnected,
    }),

    ...mapGetters('sync', ['isSyncInProgress', 'isSyncCompleted', 'isSyncInFailureState']),
    bottomNav() {
      return this.theme.bottomNav;
    },

    textColor() {
      return this.bottomNav.color + '--text';
    },
    /**
     * A sync process runs also when disconnected or if the server cannot be contacted
     * However this should not be displayed as sync
     */
    realSyncInProgress() {
      return this.isSynchronizing && this.isConnected && this.isServerReachable;
    },

    syncStateIcon() {
      if (this.isSyncCompleted) return 'mdi-check-circle';
      if (this.isSyncInFailureState) return 'mdi-alert';
      return null;
    },
    syncText() {
      if (this.isDisconnected || !this.realSyncInProgress) {
        return this.lastSyncDateTime ? dateUtils.formatAsLocaleShortDateTime(new Date(this.lastSyncDateTime)) : '';
      }
      return this.currentSync ? this.currentSync.toUpperCase() : 'sync';
    },
    // kind of hidden feature to display sync process also when offline, that is the engine updates the store from cache

    syncHint() {
      if (this.isSynchronizing) {
        if (this.realSyncInProgress) {
          return 'Synchronizing ' + this.syncText;
        }

        return this.currentSync ? 'offline "sync" from cache ' + this.currentSync : '';
      }
      return 'Sync state hint';
    },

    badgeBackgroundColor() {
      return this.isSyncCompleted ? this.successColor : this.isSyncInFailureState ? 'warning' : null;
    },
  },
  methods: {
    syncIfPossible() {
      if (!this.isDisconnected) {
        console.debug('syncIfPossible');
        this.$store.dispatch('sync/synchronizeAppIfNotSynching', {});
      }
    },
    color(condition) {
      return condition ? this.bottomNav.activeColor : this.bottomNav.color;
    },
  },
};
</script>

<style scoped>
.sync-badge {
  position: absolute;
  top: 20%;
  left: calc(50% - 23px);
  width: 30px;
  height: 30px;
  transform: translateX(9px) translateY(-10px);
}

.server-connection,
.connectivity-type,
.internet-connection,
.sync {
  position: absolute;
  top: 0px;
  width: 20px;
  height: 20px;
  display: flex;
}

.connectivity-type {
  height: 20px;
  top: 4px;
  left: 4px;
  font-size: 9px;
}

.internet-connection {
  left: 24px;
}

.server-connection {
  left: 60px;
  top: -2px;
}

.sync {
  left: 44px;
}

.sync-text,
.sync-duration {
  position: absolute;
  left: 4px;
  top: 25px;
  right: 0;
  bottom: 0;
  font-size: 9px;
  text-align: center;
}

.sync-duration {
  top: 40px;
}

.vertical-separator {
  position: absolute;
  top: 0px;
  left: 0;
  width: 1px;
  bottom: 0;
}
</style>
